import GoogleAnalytics from "react-ga4";
import { datadogRum } from "@datadog/browser-rum";
import userflow from "userflow.js";
import { ENV_OPTIONS } from "@constants";

/**
 * Analytics Service for tracking events across the application
 * Provides a centralized interface for logging events to multiple analytics platforms
 */
export default class AnalyticsService {
	static initialize() {
		/**
		 * Only initialize google
		 * Analytics when useAnalytics
		 * is enabled for the current
		 * environment
		 */
		if (ENV_OPTIONS.useAnalytics) {
			GoogleAnalytics.initialize(ENV_OPTIONS.analyticsKey);
		}
	}

	/**
	 * Track a custom event across all analytics platforms
	 * @param {Object} eventData - Event data object
	 * @param {string} eventData.category - Event category
	 * @param {string} eventData.action - Event action
	 * @param {string} [eventData.label] - Event label (optional)
	 * @param {number} [eventData.value] - Event value (optional)
	 * @param {Object} [eventData.customParameters] - Additional custom parameters
	 * @param {Object} [eventData.user] - User information
	 * @param {boolean} [eventData.sendToDatadog=true] - Whether to send to Datadog
	 * @param {boolean} [eventData.sendToUserFlow=true] - Whether to send to UserFlow
	 */
	static trackEvent(eventData) {
		const {
			category,
			action,
			label,
			value,
			customParameters = {},
			user,
			sendToDatadog = false,
			sendToUserFlow = false,
		} = eventData;

		const baseEventData = {
			...(customParameters && typeof customParameters === "object" ? customParameters : {}),
			category,
			...(label !== undefined && label !== null && { label }),
			...(typeof value === "number" && { value }),
		};
		// Only track if analytics is enabled
		if (!ENV_OPTIONS.useAnalytics) {
			return;
		}

		// Prepare base event data

		// Add user information if available
		if (user) {
			baseEventData.userId = user.Id;
		}

		// Send to Google Analytics
		try {
			GoogleAnalytics.event(action, baseEventData);
		} catch (error) {
			// Do nothing
		}

		// Send to Datadog RUM
		if (sendToDatadog) {
			try {
				datadogRum.addAction(`${category}_${action}`, {
					...baseEventData,
					timestamp: new Date().toISOString(),
				});
			} catch (error) {
				// Do nothing
			}
		}

		// Send to UserFlow
		if (sendToUserFlow) {
			try {
				userflow.track(`${category}_${action}`, {
					...baseEventData,
					timestamp: new Date().toISOString(),
				});
			} catch (error) {
				// Do nothing
			}
		}
	}

	static trackPage(page, user) {
		/**
		 * If Analytics is not enabled
		 * we don't track any page
		 */
		if (!ENV_OPTIONS.useAnalytics) {
			return;
		}

		GoogleAnalytics.set({
			page,
			userId: user?.Id,
		});
		GoogleAnalytics.send({ hitType: "pageview", page });
	}
}
